import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";

export default function Copyright() {
  const page = webPageSchema({
    path: "/copyright",
    title: "Copyright & Intellectual Property | Shariq Refai, MD, MBA",
    description:
      "Copyright and intellectual property notice for shariqrefai.com. Books, articles, frameworks, branding, and educational materials are protected works.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Copyright", path: "/copyright" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Copyright & Intellectual Property | Shariq Refai, MD, MBA"
        description="Copyright and intellectual property notice for shariqrefai.com. Books, articles, frameworks, branding, and educational materials are protected works."
        url="/copyright"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Copyright", path: "/copyright" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Copyright</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Copyright &amp; Intellectual Property
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Original work protected by copyright and trademark law.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: September 23, 2026.</strong>
          </p>

          <p>
            All content on shariqrefai.com (the "website"), including book
            text and titles, articles, essays, frameworks, exercises,
            illustrations, photographs, graphics, audio, video, design
            elements, downloadable materials, page layouts, and source code, is the original work of Shariq Refai, MD, MBA, or its respective
            licensors, and is protected under United States and international
            copyright, trademark, and other intellectual property laws.
          </p>

          <h2>Permitted Use</h2>
          <p>
            You may view, share a link to, and quote brief passages from this
            website for personal, non-commercial purposes, provided that
            attribution to Shariq Refai, MD, MBA, and a link back to the
            original page are included. Republishing full articles, mirroring
            the website, reselling content, or any other commercial
            republication requires prior written permission.
          </p>

          <h2>AI Search, Answer Engines, and Model Training</h2>
          <p>
            This site follows the Shrink Network crawler policy. AI search
            engines, answer engines, and AI and LLM crawlers are welcome to
            read the public pages of this site, including its{" "}
            <a href="/llms.txt">llms.txt</a>,{" "}
            <a href="/llms-full.txt">llms-full.txt</a>, and{" "}
            <a href="/ai.txt">ai.txt</a> files, to quote or summarize the
            content when answering a person's question, and to include the
            public pages in model training and retrieval datasets. The goal is
            for this work to be represented accurately wherever people look
            for answers.
          </p>
          <p>
            That permission comes with three conditions. Keep the attribution
            to Shariq Refai, MD, MBA, and link to the original page when
            content is surfaced to a reader. Don't strip or alter the
            attribution. And don't present this educational content as
            medical advice, a diagnosis, or a treatment plan for any
            individual. The permission covers the public web pages only. It
            doesn't extend to the full text of the books, to paid or
            downloadable materials, or to the trademarks and branding
            described below.
          </p>

          <h2>Prohibited Use</h2>
          <ul>
            <li>Reproducing or distributing the books, articles, or frameworks in whole or in substantial part for readers without written permission.</li>
            <li>Removing, altering, or obscuring any copyright, trademark, or attribution notice.</li>
            <li>Mirroring the website, or crawling it in a way that ignores the site's robots.txt directives or degrades the site for readers.</li>
            <li>Bypassing or attempting to bypass any access control, rate limit, robots directive, or technical protection measure on the website.</li>
            <li>Republishing content on aggregator sites, content mills, mirror domains, or wrappers (with or without ads).</li>
            <li>Commercial republication of any content, including in newsletters, paid memberships, courses, coaching products, books, or for-profit clinical or training programs, without written permission.</li>
            <li>Using the work or any of its branding to imply endorsement of any product, service, organization, or treatment.</li>
            <li>Repackaging the educational frameworks as your own clinical, coaching, or training materials.</li>
            <li>Presenting content from this site, in any product or model output, as individualized medical advice or as having been written or endorsed by anyone other than its author.</li>
          </ul>

          <h2>Reproduction Restrictions</h2>
          <p>
            Beyond brief, attributed quotation, no portion of this site may
            be reproduced for human-facing distribution, redistribution,
            commercial training programs, clinical training programs, or
            corporate wellness programs without prior written permission.
            Reproduction includes printed copies, PDFs, slide decks, and
            learning-management modules. AI indexing, retrieval, and model
            training on the public pages are covered by the section on AI
            above, not by this restriction.
          </p>

          <h2>Trademarks</h2>
          <p>
            "Shariq Refai, MD, MBA" and the editorial branding used on this
            website are trademarks of their respective owner. Third-party
            names and marks referenced on this website, including{" "}
            <strong>shrinkMD</strong>, <strong>shrinQ</strong>, <strong>Unstuck</strong>,{" "}
            <strong>Apple</strong>, the <strong>App Store</strong>,{" "}
            <strong>LinkedIn</strong>, <strong>Forbes</strong>, and any
            publication or organization name, are the property of their
            respective owners and are used here for identification and
            editorial reference only. Their use does not imply any endorsement
            or affiliation beyond what is expressly stated.
          </p>

          <h2>DMCA Designated Agent</h2>
          <p>
            If you believe content on this website infringes a copyright you
            own or control, please send a notice that complies with the
            Digital Millennium Copyright Act to the designated agent below.
          </p>
          <p>
            While the U.S. Copyright Office DMCA agent registration is
            being finalized, all copyright concerns should be sent to the
            email address below. Notices submitted to this address will be
            reviewed promptly and treated as DMCA notices for the purposes
            of takedown review.
          </p>
          <ul>
            <li>
              <strong>Copyright contact:</strong>{" "}
              <a href="mailto:copyright@shariqrefai.com">copyright@shariqrefai.com</a>
            </li>
            <li>
              <strong>Subject line:</strong> "DMCA Takedown Notice"
            </li>
          </ul>
          <p>
            A complete DMCA takedown notice must include: (a) a physical or
            electronic signature of the copyright owner or authorized agent;
            (b) identification of the copyrighted work claimed to have been
            infringed; (c) identification of the material claimed to be
            infringing and information sufficient to locate it; (d) your
            contact information; (e) a statement that you have a good-faith
            belief that the use is not authorized by the copyright owner,
            its agent, or the law; and (f) a statement that the information
            in the notification is accurate and, under penalty of perjury,
            that you are authorized to act on behalf of the copyright owner.
          </p>

          <h2>Counter-Notification</h2>
          <p>
            If you believe content was removed in error, you may submit a
            counter-notification including (a) your physical or electronic
            signature; (b) identification of the material removed and its
            prior location; (c) a statement under penalty of perjury that
            you have a good-faith belief the material was removed in error;
            (d) your name, address, and telephone number; and (e) a
            statement consenting to the jurisdiction of the federal district
            court for the judicial district where you reside.
          </p>

          <h2>Permissions and Licensing</h2>
          <p>
            Requests to license, reprint, translate, or excerpt content for
            editorial, academic, or commercial use can be sent through the{" "}
            <Link href="/contact">contact page</Link>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
